import datapoints from '../datapoints';
import isEventsSessionStorageStale from '../utils/isEventsSessionStorageStale';
import reloadCalendarEventsInCache from '../store/apis/reloadCalendarEventsInCache';
import { addDays, endOfDay, getTimestamp, OwaDate, startOfDay, today } from 'owa-datetime';
import { cacheReloaded, eventEmitter } from 'mini-cal-event-emitters';
import { createAppContext } from 'mini-app-context';
import { DAYS_TO_FETCH_ON_INIT_LOAD } from '../utils/eventsStoreConstants';
import { traceAndReject } from 'error-handling';
import { getTimeFromFetchStart } from 'mini-performance/lib/utils/getTimeFromFetchStart';

const isInitializationToBeDone = true;

/** Exported for testing purposes */
export const getGlobalState = () => ({
    isInitializationToBeDone,
});

const setIsInitializationToBeDone = (newVal: boolean) => {
    getGlobalState().isInitializationToBeDone = newVal;
};

/**
 * Intialize events cache with current day as pivot
 * @param sessionStorageSupported check if sessionStorage is supported
 * @param sessionStorageLastUpdateTime sessionStorage last update time.
 */
export default async function initializeCalendarEventsCache(
    sessionStorageSupported: boolean,
    sessionStorageLastUpdateTime: string
) {
    if (getGlobalState().isInitializationToBeDone) {
        const initializeCtx: any = createAppContext();
        initializeCtx?.signal(datapoints.DAL.ICES.REQUEST, { time: getTimeFromFetchStart() });
        const pivotDate = today();
        try {
            if (sessionStorageSupported) {
                const isCacheNotStale = !isEventsSessionStorageStale(sessionStorageLastUpdateTime);
                initializeCtx?.signal(datapoints.DAL.RCE.SESSION_STORAGE_EVENT_STALE, {
                    isCacheNotStale: isCacheNotStale,
                });
                if (isCacheNotStale) {
                    await reloadEventsFromSessionStorage(pivotDate, initializeCtx);
                    // notify
                } else {
                    setIsInitializationToBeDone(false);
                    await reloadEventsFromNetwork(pivotDate, initializeCtx);
                }
            } else {
                setIsInitializationToBeDone(false);
                // session storage not supported.
                await reloadEventsFromNetwork(pivotDate, initializeCtx);
                eventEmitter.emit(cacheReloaded, null);
            }
            // TODO: Resolve the promise with different enums in the above if else blocks
            // indicating the callers whether the events are fetched from network or sessionStorage etc.
            return Promise.resolve();
        } catch (error) {
            return traceAndReject('initializeCalendarEventsStore', error);
        }
    }
}

/**
 * Fetch events from network around pivot and add to cache.
 * @param pivotDate pivot date.
 * @returns empty promise
 */
function reloadEventsFromNetwork(pivotDate: OwaDate, ctx: any): Promise<void> {
    ctx?.signal(datapoints.DAL.ICES.RELOAD_FROM_NETWORK);
    return reloadCalendarEventsInCache(
        {
            start: getTimestamp(
                startOfDay(addDays(pivotDate, -DAYS_TO_FETCH_ON_INIT_LOAD / 2 + 1))
            ),
            end: getTimestamp(endOfDay(addDays(pivotDate, DAYS_TO_FETCH_ON_INIT_LOAD / 2))),
        },
        undefined, // folder id
        ctx
    );
}

/**
 * Fetch events from sessionStorage around pivot and add them to cache.
 * @param pivotDate pivot date.
 * @returns empty promise
 */
function reloadEventsFromSessionStorage(pivotDate: OwaDate, ctx: any): Promise<void> {
    ctx?.signal(datapoints.DAL.ICES.RELOAD_FROM_SESSION_STORAGE);
    return reloadCalendarEventsInCache(
        {
            start: getTimestamp(
                startOfDay(addDays(pivotDate, -DAYS_TO_FETCH_ON_INIT_LOAD / 2 + 1))
            ),
            end: getTimestamp(endOfDay(addDays(pivotDate, DAYS_TO_FETCH_ON_INIT_LOAD / 2))),
        },
        undefined,
        ctx,
        true
    );
}
